import { InvitationItem } from "@nimble/recommendation-client/types";
import { useCallback, useMemo, useState } from "react";

import {
  InvitationItemState,
  MessageState,
} from "@/app/(discover)/candidate/invites/lib/invites-types";

function determineInvitationState({
  isResponded,
  isSelected,
  isFirstAvailable,
}: {
  invitation: InvitationItem;
  isResponded: boolean;
  isSelected: boolean;
  isFirstAvailable: boolean;
}): MessageState {
  if (isResponded) return "responded";
  if (isSelected) return "selected";
  if (isFirstAvailable) return "available";
  return "locked";
}

export default function useInvitationsControl(
  invitations: InvitationItem[],
  initialSelectedUuid: string | null,
  closeAfterRespond: boolean,
) {
  const [respondedInvitationIds, setRespondedInvitationIds] = useState<
    Set<string>
  >(new Set());

  const [selectedUuid, setSelectedUuid] = useState<string | null>(
    initialSelectedUuid,
  );

  const invitationsState: InvitationItemState[] = useMemo(() => {
    const firstAvailableIndex = invitations.findIndex(
      (inv) => !respondedInvitationIds.has(inv.matching_uuid),
    );

    return invitations.map((invitation, index) => {
      const state = determineInvitationState({
        invitation,
        isResponded: respondedInvitationIds.has(invitation.matching_uuid),
        isSelected: invitation.matching_uuid === selectedUuid,
        isFirstAvailable: index === firstAvailableIndex,
      });

      return { invitation, state };
    });
  }, [invitations, respondedInvitationIds, selectedUuid]);

  const selectedInvitation = useMemo(
    () =>
      invitationsState.find(
        (item) => item.invitation.matching_uuid === selectedUuid,
      ) ?? null,
    [invitationsState, selectedUuid],
  );

  const onCloseInvitation = useCallback(() => {
    setSelectedUuid(null);
  }, []);

  const onRespond = useCallback(() => {
    if (!selectedUuid) return;

    setRespondedInvitationIds((prev) => new Set(prev).add(selectedUuid));

    const currentIndex = invitationsState.findIndex(
      (item) => item.invitation.matching_uuid === selectedUuid,
    );
    const nextInvitation = closeAfterRespond
      ? null
      : invitationsState[currentIndex + 1];

    setSelectedUuid(nextInvitation?.invitation.matching_uuid ?? null);
  }, [invitationsState, selectedUuid, closeAfterRespond]);

  const onSelect = useCallback(
    (invitationState: InvitationItemState | null) => {
      setSelectedUuid(invitationState?.invitation.matching_uuid ?? null);
    },
    [],
  );

  return {
    invitationsState,
    selectedInvitation,
    onCloseInvitation,
    onSelect,
    onRespond,
  };
}
